import { Grid, GridItem, GridItemProps, HStack, Text } from '@chakra-ui/react';
import React from 'react';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { dollarNumberWithCommas } from '~/packages/common-frontend/utils';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

const FundingImpactOverviewRowShell = ({
  label,
  before,
  after,
  lastCol,
  labelColStyle,
  beforeColStyle,
  afterColStyle,
  lastColStyle,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
  labelColStyle?: GridItemProps['style'];
  beforeColStyle?: GridItemProps['style'];
  afterColStyle?: GridItemProps['style'];
  lastColStyle?: GridItemProps['style'];
}) => {
  return (
    <>
      <GridItem
        colSpan={5}
        display="flex"
        alignItems="center"
        style={labelColStyle}
      >
        {label}
      </GridItem>
      <GridItem
        colSpan={4}
        display="flex"
        alignItems="center"
        justifyContent={after ? 'flex-end' : undefined}
        style={beforeColStyle}
      >
        {before}
      </GridItem>
      {after && (
        <GridItem colSpan={4} style={afterColStyle}>
          {after}
        </GridItem>
      )}
      <GridItem colSpan={1} display="flex" style={lastColStyle}>
        {lastCol}
      </GridItem>
    </>
  );
};

const TopLevelFundingImpactOverviewRow = ({
  label,
  before,
  after,
  lastCol,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
}) => {
  return (
    <FundingImpactOverviewRowShell
      label={
        <>
          <Icon icon="circle-dollar" size="16px" />
          {label}
        </>
      }
      before={before}
      after={after}
      lastCol={lastCol}
      labelColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        color: colors.black[95],
        gap: '16px',
        padding: '16px',
      }}
      beforeColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        color: colors.black[95],
        padding: '16px',
      }}
      afterColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        backgroundColor: colors.aurora[900],
        color: 'white',
        padding: '16px',
        textAlign: 'right',
      }}
    />
  );
};

export const FundingImpact = () => {
  const { user } = useUserAuthOrThrow();
  const {
    context: {
      allowedActions,
      data: {
        fundingImpact: {
          commitment,
          maximumAvailableAmount,
          outstandingBalance,
          drawAvailability, // only for mutable view
          recycleAvailability,
        },
      },
    },
  } = useFundingRequestTemplateContextV1();

  return (
    <>
      <Grid
        templateColumns="repeat(14, 1fr)"
        templateRows="repeat(1, 56px)"
        rowGap="16px"
        color={colors.black[95]}
      >
        <GridItem colSpan={5} />
        <GridItem
          colSpan={4}
          borderBottom="1px solid"
          borderColor={colors.gray[50]}
          textAlign="right"
          p="16px"
          fontWeight="600"
        >
          Before
        </GridItem>

        <GridItem
          colSpan={4}
          borderBottom="1px solid"
          borderColor={colors.gray[50]}
          alignSelf="end"
          bg={colors.aurora[900]}
          color="white"
          fontWeight="600"
          p="16px"
          textAlign="right"
        >
          After
        </GridItem>

        <GridItem
          colSpan={1}
          borderBottom="1px solid"
          borderColor={colors.gray[50]}
        />
        <TopLevelFundingImpactOverviewRow
          label={<Text>Commitment</Text>}
          before={dollarNumberWithCommas(commitment.amount)}
          after={dollarNumberWithCommas(commitment.amount)}
        />
        {maximumAvailableAmount && (
          <TopLevelFundingImpactOverviewRow
            label={
              <HStack w="100%" alignItems="center" gap="16px">
                <Text>Maximum Available Amount</Text>
                <CalculationTerminologiesPopover termKeyLabel="maximumAvailableAmount" />
              </HStack>
            }
            before={dollarNumberWithCommas(maximumAvailableAmount.before)}
            after={dollarNumberWithCommas(maximumAvailableAmount.after)}
          />
        )}
        <TopLevelFundingImpactOverviewRow
          label={<Text>Outstanding Balance</Text>}
          before={dollarNumberWithCommas(outstandingBalance.before)}
          after={dollarNumberWithCommas(outstandingBalance.after)}
        />
        {allowedActions === 'mutable' &&
          user.organization.slug !== 'parafin' && (
            <TopLevelFundingImpactOverviewRow
              label={<Text>Draw Availability</Text>}
              before={dollarNumberWithCommas(drawAvailability.before)}
              after={dollarNumberWithCommas(drawAvailability.after)}
            />
          )}
        {allowedActions === 'mutable' &&
          recycleAvailability &&
          user.organization.slug !== 'parafin' && (
            <TopLevelFundingImpactOverviewRow
              label={<Text>Recycle Availability</Text>}
              before={dollarNumberWithCommas(recycleAvailability.before)}
              after={dollarNumberWithCommas(recycleAvailability.after)}
            />
          )}
      </Grid>
    </>
  );
};
